import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  Pagination,
  Select,
  StatusBadge,
  TextInput,
  Tag,
  ToggleSwitch,
  formatNumber,
  useLocale,
  type DataTableColumn,
  type SortDirection,
} from '@ministry/ui/internal';

import { listFiles, type FileListRow, type ListFilesParams } from '../lib/files.js';
import { ApiError } from '../lib/api-client.js';
import { fileStageEntry, OVERDUE_FLAG, CONFIDENTIALITY_LEVELS, isAboveInternalClearance, FILTERABLE_STATUS_CODES } from '../lib/file-lifecycle.js';
import { useSession } from '../lib/session.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatDueDisplay, formatRegisteredDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { BulkReassignModal } from '../components/FileActionModals.js';
import { ForbiddenState } from '../components/ForbiddenState.js';

const PAGE_SIZE = 25;

interface Filters {
  q: string;
  statusCode: string;
  confidentialityLevel: string;
  isOverdue: boolean;
}

const EMPTY_FILTERS: Filters = { q: '', statusCode: '', confidentialityLevel: '', isOverdue: false };

/** `-registeredAt` / `-dueAt` / `-trackingCode` etc. — the only sortable fields the running server's Zod schema whitelists. */
const SORT_FIELD_FOR_COLUMN: Record<string, 'registeredAt' | 'dueAt' | 'trackingCode'> = {
  trackingCode: 'trackingCode',
  dueDate: 'dueAt',
  registeredDateBs: 'registeredAt',
};

function toServerSort(columnKey: string, direction: SortDirection): ListFilesParams['sort'] {
  const field = SORT_FIELD_FOR_COLUMN[columnKey];
  if (!field || !direction) return '-registeredAt';
  return direction === 'desc' ? (`-${field}` as ListFilesParams['sort']) : field;
}

/**
 * SCREEN_SPECS.md I6. Loading/error/populated states come from `DataTable` itself
 * (`loading`/`error`/`onRetry` props); this component supplies the two DISTINCT empty
 * copies the spec calls for ("no files exist yet in this scope" vs. "no files match your
 * filters") via `emptyTitle`/`emptyBody`/`emptyAction`, which `DataTable` does not decide
 * on its own.
 */
export function FileRegisterPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = React.useState(1);
  const [sortKey, setSortKey] = React.useState<string | undefined>('registeredDateBs');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

  /**
   * "Bulk reassign has no UI." A `Map` (not a bare `Set<string>`) so a selection survives
   * paging: `DataTable`'s checkboxes only exist for the rows currently rendered, but
   * `BulkReassignModal` needs full `FileListRow`s (tracking codes, not bare uuids) for
   * every file selected, even the ones on a page the user has since paged away from.
   */
  const [selectedRows, setSelectedRows] = React.useState<Map<string, FileListRow>>(new Map());
  const [bulkReassignOpen, setBulkReassignOpen] = React.useState(false);
  /**
   * A genuine bug caught only by driving this live: `BulkReassignModal`'s OWN results
   * view (after a successful submit) renders tracking codes by looking each
   * `item.fileId` up in its `rows` prop. Clearing `selectedRows` the moment the mutation
   * succeeds (the obvious place to put it) ALSO cleared the very `rows` array the modal
   * was still using to label its own just-finished results, which was still on screen —
   * so a real reassignment succeeded but the confirmation showed a bare, unreadable uuid
   * instead of "MW-2083-000007". A snapshot frozen at the moment the modal opens, and
   * cleared only when it fully closes, keeps the results view stable regardless of what
   * the underlying selection state does in the meantime.
   */
  const [bulkReassignRows, setBulkReassignRows] = React.useState<FileListRow[]>([]);
  const canBulkReassign = session?.permissions.includes('movement.reassign') ?? false;

  // Debounced search — one request per pause in typing, not one per keystroke.
  const [qInput, setQInput] = React.useState('');
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setFilters((prev) => (prev.q === qInput ? prev : { ...prev, q: qInput }));
      setPage(1);
    }, 350);
    return () => window.clearTimeout(handle);
  }, [qInput]);

  const hasActiveFilters = filters.q !== '' || filters.statusCode !== '' || filters.confidentialityLevel !== '' || filters.isOverdue;

  const query = useQuery({
    queryKey: ['files', filters, page, sortKey, sortDirection],
    queryFn: ({ signal }) =>
      listFiles(
        {
          page,
          pageSize: PAGE_SIZE,
          sort: toServerSort(sortKey ?? 'registeredDateBs', sortDirection),
          q: filters.q || undefined,
          statusCode: filters.statusCode || undefined,
          confidentialityLevel: filters.confidentialityLevel || undefined,
          isOverdue: filters.isOverdue || undefined,
        },
        signal,
      ),
  });

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setQInput('');
    setPage(1);
  };

  // API_CONTRACT.md §7: an in-tenant 403 splits four ways and each means something
  // different to the person reading it — never collapsed into one message. Shared with
  // every other screen via `ForbiddenState` (SCREEN_SPECS.md I24) — this screen used to
  // handle only `FORBIDDEN_ROLE`/`FORBIDDEN_SCOPE` itself and fell through to a generic
  // banner for `_OWNERSHIP`/`_CLEARANCE`, the exact partial split I24's shared-treatment
  // note calls out.
  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('fileRegister.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];
  const pageMeta = query.data?.page;
  const anyOverdueOnPage = rows.some((row) => row.isOverdue);

  const handleSelectionChange = (ids: Set<string>) => {
    setSelectedRows((prev) => {
      const next = new Map<string, FileListRow>();
      for (const id of ids) {
        const row = prev.get(id) ?? rows.find((r) => r.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });
  };

  const allColumns: DataTableColumn<FileListRow>[] = [
    {
      key: 'trackingCode',
      header: t('fileRegister.column.trackingCode'),
      visibleFrom: 'always',
      sortable: true,
      cardSlot: 'title',
      render: (row) => <span className="whitespace-nowrap font-mono">{row.trackingCode}</span>,
      mdRender: (row) => (
        <span className="inline-flex items-center gap-1 whitespace-nowrap font-mono">
          {isAboveInternalClearance(row.confidentialityLevel) ? (
            <span aria-hidden="true" title={t('fileRegister.column.confidentiality')}>
              🔒
            </span>
          ) : null}
          {row.trackingCode}
        </span>
      ),
    },
    {
      key: 'confidentiality',
      header: t('fileRegister.column.confidentiality'),
      visibleFrom: 'lg',
      cardSlot: 'confidentialityIcon',
      render: (row) => {
        const entry = CONFIDENTIALITY_LEVELS.find((l) => l.code === row.confidentialityLevel);
        const label = entry ? (locale === 'ne' ? entry.labelNe : entry.labelEn) : row.confidentialityLevel;
        return isAboveInternalClearance(row.confidentialityLevel) ? <Tag>🔒 {label}</Tag> : <span className="text-text-muted">{label}</span>;
      },
    },
    {
      key: 'statusCode',
      header: t('fileRegister.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={fileStageEntry(row.statusCode)} />,
    },
    // Overdue is an orthogonal flag "appended to any [stage], not a stage of its own"
    // (DESIGN_SYSTEM.md §9.1). Kept as its own column, but included below ONLY when at
    // least one row on the current page is actually overdue — a dedicated column that is
    // blank for every row on the page was a named defect ("one column renders entirely
    // empty"); when it IS relevant, a single narrow flag column is clearer than folding a
    // second badge into Status on every row.
    {
      key: 'overdue',
      header: t('fileRegister.column.overdue'),
      visibleFrom: 'always',
      cardSlot: 'overdueBadge',
      render: (row) => (row.isOverdue ? <StatusBadge entry={OVERDUE_FLAG} variant="solid" /> : null),
    },
    {
      key: 'subject',
      header: t('fileRegister.column.subject'),
      visibleFrom: 'always',
      cardSlot: 'line2',
      // Column priority order (§5.1) puts Subject — "what the file actually is" — ahead
      // of every column but Tracking Code and Status; it is the one column that should
      // absorb the table's leftover width (ux-review defect: "the subject … is pushed to
      // the far right" while the tracking code took ~320px it didn't need).
      grow: true,
      render: (row) =>
        row.subjectRedacted ? (
          <span className="italic text-text-muted">{t('fileRegister.subjectRedacted')}</span>
        ) : (
          (row.subject ?? t('common.notAvailable'))
        ),
    },
    {
      key: 'dueDate',
      header: t('fileRegister.column.dueDate'),
      visibleFrom: 'md',
      sortable: true,
      cardSlot: 'line4',
      // BS-primary + AD-secondary in parentheses (DESIGN_SYSTEM.md §3) reads as one
      // fairly long phrase — a wide-enough `minWidth` keeps it to two clean lines
      // instead of breaking mid-date into three.
      minWidth: '9rem',
      render: (row) => (row.dueAt ? formatDueDisplay(row.dueAt, locale) : t('fileRegister.noDueDate')),
    },
    {
      key: 'assignedTo',
      header: t('fileRegister.column.assignedTo'),
      visibleFrom: 'md',
      cardSlot: 'line4',
      render: (row) => {
        if (!row.currentHolderUserId) return t('common.unassigned');
        if (session?.user.id === row.currentHolderUserId) return t('common.you');
        // `GET /files` returns only `currentHolderUserId` (a uuid) — no display name,
        // unlike `GET /files/{id}/movements`'s `to.userName`. A raw hex fragment
        // (`019243b0…`) is not an acceptable render for a government register (defect
        // confirmed in UX_REVIEW.md), so this shows the honest, non-technical fact the
        // client actually has — "assigned, name not shown here" — instead of a
        // meaningless ID. Resolving 25 uuids to names client-side would mean 25 extra
        // requests per page, which is the wrong fix; the real fix is a
        // `currentHolderName` field on this endpoint, flagged in the handback.
        return <span className="text-text-muted">{t('fileRegister.assignedToOther')}</span>;
      },
    },
    {
      key: 'section',
      header: t('fileRegister.column.section'),
      visibleFrom: 'md',
      cardSlot: 'line3',
      render: (row) => (locale === 'ne' ? row.section.nameNe : row.section.nameEn),
    },
    {
      key: 'registeredDateBs',
      header: t('fileRegister.column.registeredDate'),
      visibleFrom: 'lg',
      sortable: true,
      minWidth: '9rem',
      render: (row) => formatRegisteredDisplay(row.registeredDateBs, locale),
    },
  ];

  const columns = anyOverdueOnPage ? allColumns : allColumns.filter((c) => c.key !== 'overdue');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('fileRegister.title')}
        description={
          pageMeta
            ? t('fileRegister.resultCount', { count: formatNumber(pageMeta.totalItems, { locale }) })
            : undefined
        }
        actions={
          session?.permissions.includes('file.register') ? (
            <Button onClick={() => navigate(ROUTES.fileNew)}>{t('fileIntake.newFile')}</Button>
          ) : undefined
        }
      />

      {/*
        Filter bar is deliberately the LIGHT element on this screen now — a bottom rule,
        no border box, no shadow — and the table below carries the visual weight instead
        (its own container, from `DataTable`). This was backwards before: a heavy `Card`
        around the filters, and a table floating with no container of its own at all
        (ux-review defect: "visual weight is backwards").
      */}
      <div className="flex flex-wrap items-end gap-3 border-b border-border-decorative pb-4">
        <div className="min-w-[14rem] flex-1">
          <TextInput
            id="file-register-search"
            label={t('fileRegister.searchLabel')}
            placeholder={t('fileRegister.searchPlaceholder')}
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            id="file-register-status"
            label={t('fileRegister.filterStatus')}
            placeholder={t('fileRegister.filterStatusAll')}
            value={filters.statusCode || undefined}
            onValueChange={(value) => {
              setFilters((prev) => ({ ...prev, statusCode: value }));
              setPage(1);
            }}
            options={FILTERABLE_STATUS_CODES.map((code) => ({
              value: code,
              label: locale === 'ne' ? fileStageEntry(code).ne : fileStageEntry(code).en,
            }))}
          />
        </div>
        <div className="w-48">
          <Select
            id="file-register-confidentiality"
            label={t('fileRegister.filterConfidentiality')}
            placeholder={t('fileRegister.filterConfidentialityAll')}
            value={filters.confidentialityLevel || undefined}
            onValueChange={(value) => {
              setFilters((prev) => ({ ...prev, confidentialityLevel: value }));
              setPage(1);
            }}
            options={CONFIDENTIALITY_LEVELS.map((level) => ({
              value: level.code,
              label: locale === 'ne' ? level.labelNe : level.labelEn,
            }))}
          />
        </div>
        <ToggleSwitch
          id="file-register-overdue-only"
          label={t('fileRegister.filterOverdueOnly')}
          checked={filters.isOverdue}
          onCheckedChange={(checked) => {
            setFilters((prev) => ({ ...prev, isOverdue: checked }));
            setPage(1);
          }}
        />
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t('fileRegister.clearFilters')}
          </Button>
        ) : null}
      </div>

      {/*
        "Bulk reassign has no UI" — a client function and a server endpoint existed with
        nothing calling either. Row selection is `DataTable`'s own existing (unused)
        `selectedIds`/`onSelectionChange` — gated on `movement.reassign` the same way
        every other write action on this screen is gated on its own permission, never on
        `roleCode`. This bar only appears once something is actually selected, so it
        costs nothing for the far more common case of a viewer who never selects a row.
      */}
      {canBulkReassign && selectedRows.size > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface-selected p-3">
          <span className="text-sm font-semibold text-text-primary">
            {t('fileRegister.bulkAction.selectedCount', { count: formatNumber(selectedRows.size, { locale }) })}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedRows(new Map())}>
              {t('fileRegister.bulkAction.clearSelection')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setBulkReassignRows([...selectedRows.values()]);
                setBulkReassignOpen(true);
              }}
            >
              {t('fileRegister.bulkAction.reassign')}
            </Button>
          </div>
        </div>
      ) : null}

      <DataTable
        caption={t('fileRegister.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        onRowClick={(row) => navigate(ROUTES.fileDetail(row.id))}
        selectedIds={canBulkReassign ? new Set(selectedRows.keys()) : undefined}
        onSelectionChange={canBulkReassign ? handleSelectionChange : undefined}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={(key, direction) => {
          setSortKey(key);
          setSortDirection(direction);
          setPage(1);
        }}
        emptyTitle={hasActiveFilters ? t('fileRegister.emptyFiltered.title') : t('fileRegister.emptyScope.title')}
        emptyBody={hasActiveFilters ? t('fileRegister.emptyFiltered.body') : t('fileRegister.emptyScope.body')}
        emptyAction={
          hasActiveFilters ? (
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              {t('common.clearFilters')}
            </Button>
          ) : undefined
        }
      />

      {bulkReassignOpen ? (
        <BulkReassignModal
          open
          onOpenChange={(open) => {
            setBulkReassignOpen(open);
            if (!open) setBulkReassignRows([]);
          }}
          rows={bulkReassignRows}
          onCompleted={() => {
            setSelectedRows(new Map());
            void queryClient.invalidateQueries({ queryKey: ['files'] });
          }}
        />
      ) : null}

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination page={page} pageCount={pageMeta.totalPages} onPageChange={setPage} loading={query.isFetching} />
      ) : null}
    </div>
  );
}
